'use client';

import Link from 'next/link';
import classes from './SignatureHero.module.scss';

type HeroStat = {
  label: string;
  value: string;
  helper?: string;
};

type SignatureHeroProps = {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta: string;
  primaryHref: string;
  secondaryCta?: string;
  secondaryHref?: string;
  phoneLabel?: string;
  phoneNumber?: string;
  whatsappLabel?: string;
  whatsappNumber?: string;
  badge?: string;
  badgeDescription?: string;
  stats?: HeroStat[];
  highlights?: string[];
};

export const SignatureHero = ({ data }: { data: SignatureHeroProps }) => {
  const {
    eyebrow,
    title,
    description,
    primaryCta,
    primaryHref,
    secondaryCta,
    secondaryHref,
    phoneLabel,
    phoneNumber,
    whatsappLabel,
    whatsappNumber,
    badge,
    badgeDescription,
    stats = [],
    highlights = [],
  } = data;

  return (
    <section className={classes.hero}>
      <div className={classes.grid}>
        <div className={classes.left}>
          {eyebrow && <p className={classes.eyebrow}>{eyebrow}</p>}
          <h1 className={classes.title}>{title}</h1>
          <p className={classes.description}>{description}</p>
          <div className={classes.ctas}>
            <Link href={primaryHref} className={`${classes.cta} ${classes.primary}`}>
              {primaryCta}
            </Link>
            {secondaryCta && secondaryHref && (
              <Link href={secondaryHref} className={`${classes.cta} ${classes.secondary}`}>
                {secondaryCta}
              </Link>
            )}
          </div>
          <div className={classes.contact}>
            {phoneLabel && phoneNumber && (
              <div>
                <span className={classes.contactLabel}>{phoneLabel}</span>
                <a href={`tel:${phoneNumber}`} className={classes.contactValue}>
                  {phoneNumber}
                </a>
              </div>
            )}
            {whatsappLabel && whatsappNumber && (
              <div>
                <span className={classes.contactLabel}>{whatsappLabel}</span>
                <a
                  href={`https://wa.me/${whatsappNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  className={classes.contactValue}
                >
                  {whatsappNumber}
                </a>
              </div>
            )}
          </div>
        </div>
        <div className={classes.right}>
          <div className={classes.card}>
            {badge && <div className={classes.badge}>{badge}</div>}
            {badgeDescription && <p className={classes.badgeDescription}>{badgeDescription}</p>}
            {highlights.length > 0 && (
              <ul className={classes.highlights}>
                {highlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
            {stats.length > 0 && (
              <div className={classes.stats}>
                {stats.map((stat) => (
                  <div key={`${stat.label}-${stat.value}`} className={classes.stat}>
                    <span className={classes.statValue}>{stat.value}</span>
                    <span className={classes.statLabel}>{stat.label}</span>
                    {stat.helper && <span className={classes.statHelper}>{stat.helper}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
